import React, { useState } from "react";
import { MenuItem, Select, FormControl, InputLabel, LinearProgress } from "@mui/material";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TrafficIcon from "@mui/icons-material/Traffic";
import BarChartIcon from "@mui/icons-material/BarChart";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth, appendTokenToUrl } from "../../../contexts/AuthContext";
import { useConfig } from "../../../contexts/config";
import { getGradeReportbyCourse } from "../../../services/moodle/courses";

/* ── Color maps ─────────────────────────────────────────────────────────── */

const RIESGO_COLOR = {
  Alto: "#ef4444",
  Medio: "#f59e0b",
  Bajo: "#22c55e",
};

const NIVEL_COLOR = {
  Alto: "#22c55e",
  Medio: "#f59e0b",
  Bajo: "#ef4444",
};

const ESTADO_COLOR = {
  "Verde (safe)": "#22c55e",
  "Amarillo (warning)": "#f59e0b",
  "Rojo (at risk)": "#ef4444",
};

/* ── Avatar de iniciales ────────────────────────────────────────────────── */

const StudentAvatar = ({ nombre, avatar, colors, size = 76 }) => {
  const [imgError, setImgError] = useState(false);

  const initials = nombre
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  const containerStyle = {
    width: size,
    height: size,
    borderRadius: "50%",
    flexShrink: 0,
    border: `3px solid ${colors.button_light || "#fdbf00"}`,
    boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
    overflow: "hidden",
  };

  if (avatar && !imgError) {
    return (
      <div style={containerStyle}>
        <img src={avatar} alt={nombre} onError={() => setImgError(true)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </div>
    );
  }

  return (
    <div
      style={{
        ...containerStyle,
        backgroundColor: colors.primary_light || "#1a4275",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.36,
        fontWeight: 800,
        letterSpacing: "-0.02em",
      }}
    >
      {initials}
    </div>
  );
};

/* ── Pill badge ──────────────────────────────────────────────────────────── */

const Badge = ({ children, color }) => (
  <span
    style={{
      fontSize: 11,
      fontWeight: 700,
      padding: "3px 10px",
      borderRadius: 20,
      backgroundColor: `${color}22`,
      color: color,
      border: `1px solid ${color}44`,
      whiteSpace: "nowrap",
      display: "inline-flex",
      alignItems: "center",
    }}
  >
    {children}
  </span>
);

/* ── Stat inline ─────────────────────────────────────────────────────────── */

const StatItem = ({ label, value, valueColor, isDark }) => (
  <div>
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        color: isDark ? "#64748b" : "#94a3b8",
        display: "block",
        marginBottom: 2,
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: 13,
        fontWeight: 700,
        color: valueColor || (isDark ? "#f1f5f9" : "#1e293b"),
        display: "flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      {value}
    </span>
  </div>
);

/* ── Mini info section ───────────────────────────────────────────────────── */

const MiniSection = ({ icon, title, accentColor, children, isDark }) => (
  <div
    style={{
      borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "#e2e8f0"}`,
      paddingTop: 14,
      marginTop: 14,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginBottom: 10,
      }}
    >
      {React.cloneElement(icon, {
        style: { fontSize: 15, color: accentColor },
      })}
      <span
        style={{
          fontSize: 10,
          fontWeight: 800,
          color: accentColor,
          textTransform: "uppercase",
          letterSpacing: "0.07em",
        }}
      >
        {title}
      </span>
    </div>
    {children}
  </div>
);

/* ── Main component ─────────────────────────────────────────────────────── */

export const MisHijosMisCursos = () => {
  const { colors, mode } = useTheme() || { colors: {}, mode: "light" };
  const auth = useAuth();
  const { config } = useConfig();

  const rawStudents = auth?.user?.students || [];

  const [expandedCourseId, setExpandedCourseId] = useState(null);
  const [courseGrades, setCourseGrades] = useState({});
  const [loadingGrades, setLoadingGrades] = useState({});

  const fetchCourseGrades = async (courseId, studentId) => {
    const key = `${studentId}-${courseId}`;
    if (courseGrades[key] || loadingGrades[key]) return;
    setLoadingGrades((prev) => ({ ...prev, [key]: true }));
    try {
      const result = await getGradeReportbyCourse({
        token: config.token,
        id: studentId,
        course_id: courseId,
        baseURL: config.baseURL,
      });
      const items = (result?.usergrades?.[0]?.gradeitems || []).filter((d) => d.itemname && d.itemname !== "" && !d.gradeishidden);
      setCourseGrades((prev) => ({ ...prev, [key]: items }));
    } catch (e) {
      console.error("Error fetching grades:", e);
      setCourseGrades((prev) => ({ ...prev, [key]: [] }));
    } finally {
      setLoadingGrades((prev) => ({ ...prev, [key]: false }));
    }
  };

  const toggleCourse = (courseId, studentId) => {
    const key = `${studentId}-${courseId}`;
    if (expandedCourseId === key) {
      setExpandedCourseId(null);
    } else {
      setExpandedCourseId(key);
      fetchCourseGrades(courseId, studentId);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Nunca";
    return new Date(timestamp * 1000).toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const HIJOS = rawStudents.map((student) => {
    let nivelActividad = "Alto";
    if (student.activity_level === "active") {
      nivelActividad = "Alto";
    } else if (student.activity_level === "low_activity") {
      nivelActividad = "Medio";
    } else {
      // "inactive" o "no_access_data"
      nivelActividad = "Bajo";
    }

    let estadoGeneral = "Verde (safe)";
    if (nivelActividad === "Medio") estadoGeneral = "Amarillo (warning)";
    if (nivelActividad === "Bajo") estadoGeneral = "Rojo (at risk)";

    const ALERT_PRIORITY = {
      no_access_data: 1,
      no_courses: 2,
      inactive: 3,
      low_activity: 4,
    };

    const alertasOrdenadas = [...(student.alerts || [])].sort((a, b) => (ALERT_PRIORITY[a.type] || 999) - (ALERT_PRIORITY[b.type] || 999));

    const ALERT_RISK = {
      no_access_data: "Alto",
      no_courses: "Alto",
      inactive: "Medio",
      low_activity: "Bajo",
    };

    const bajaActividad = alertasOrdenadas.map((alert) => ({
      tipo: alert.type,
      mensaje: alert.message,
      riesgo: ALERT_RISK[alert.type] || "N/A",
      nombre_curso: alert.course || "null",
    }));

    return {
      id: student.id,
      nombre: student.fullname,
      avatar: appendTokenToUrl(student.profileimageurl, auth.user.token),
      nivelActividad: nivelActividad,
      ultimoAcceso: formatDate(student.lastaccess),
      diasDesdeAcceso: student.days_since_last_access ? `${student.days_since_last_access} días` : "-",
      alertasCount: alertasOrdenadas.length,
      alertasActivas: alertasOrdenadas,
      estadoGeneral: estadoGeneral,
      bajaActividad: bajaActividad,
      cursos: (student.courses || []).map((c) => ({
        id: c.id,
        nombre: c.fullname,
        img: null,
        progreso: c.progress,
        proximaTarea: "-",
        ultimaActividad: formatDate(c.lastaccess),
      })),
    };
  });

  const [selectedHijoId, setSelectedHijoId] = useState("");

  React.useEffect(() => {
    if (HIJOS.length > 0 && !selectedHijoId) {
      setSelectedHijoId(HIJOS[0].id);
    }
  }, [HIJOS, selectedHijoId]);

  if (HIJOS.length === 0) return null;

  const hijo = HIJOS.find((h) => h.id === selectedHijoId) || HIJOS[0];

  const isDark = mode === "dark";
  const bgMain = colors.backgroundColorBody || (isDark ? "#0f172a" : "#f8fafc");
  const bgCard = colors.backgroundColorDetailCourse || (isDark ? "#1e293b" : "#ffffff");
  const textPrimary = isDark ? "#f1f5f9" : "#1e293b";
  const textSecondary = isDark ? "#94a3b8" : "#64748b";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0";
  const accent = colors.button_light || "#fdbf00";

  // const progresoColor = (p) => (p >= 80 ? "#22c55e" : p >= 50 ? "#f59e0b" : "#ef4444");

  return (
    <>
      {/* ── Responsive styles ── */}
      <style>{`
        .mhmc-grid {
          display: flex;
          gap: 20px;
          align-items: flex-start;
          flex-wrap: wrap;
        }
        .mhmc-sidebar {
          width: 100%;
        }
        .mhmc-main {
          flex: 1 1 320px;
          min-width: 0;
        }
        .mhmc-student-header {
          display: flex;
          gap: 20px;
          align-items: flex-start;
          flex-wrap: nowrap;
        }
        .mhmc-stats-row {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          gap: 8px 0;
          margin-top: 12px;
          width: 100%;
        }
        .mhmc-stat-item {
          flex: 1 1 0;
          min-width: 80px;
          padding: 0 12px;
        }
        .mhmc-stat-item:first-child {
          padding-left: 0;
        }
        .mhmc-stat-separator {
          width: 1px;
          align-self: stretch;
          background-color: rgba(148,163,184,0.25);
          flex-shrink: 0;
          margin: 4px 0;
        }
        .mhmc-info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-top: 4px;
        }
        @media (max-width: 700px) {
          .mhmc-info-grid {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 540px) {
          .mhmc-stats-row {
            flex-direction: column;
            gap: 12px;
          }
          .mhmc-stat-separator {
            display: none;
          }
          .mhmc-stat-item {
            padding: 0;
          }
        }
        .mhmc-info-sections {
          display: flex;
          flex-direction: column;
        }
        .mhmc-table-head th {
          padding: 12px 16px;
          font-size: 11px;
          font-weight: 700;
          text-align: left;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          white-space: nowrap;
        }
        .mhmc-table-body td {
          padding: 13px 16px;
          vertical-align: middle;
        }
        @media (min-width: 768px) {
          .mhmc-sidebar {
            width: clamp(200px, 20vw, 260px);
            flex-shrink: 0;
          }
        }
        @media (max-width: 540px) {
          .mhmc-student-header {
            flex-wrap: wrap;
          }
        }
        /* ── Slide panels ── */
        .mhmc-panels-wrapper {
          position: relative;
          overflow: hidden;
        }
        .mhmc-panel {
          width: 100%;
          transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
          will-change: transform;
        }
        .mhmc-panel-courses {
          transform: translateX(0);
          opacity: 1;
        }
        .mhmc-panel-courses.slide-out {
          transform: translateX(-100%);
          opacity: 0;
          position: absolute;
          top: 0; left: 0; right: 0;
          pointer-events: none;
        }
        .mhmc-panel-grades {
          transform: translateX(100%);
          opacity: 0;
          position: absolute;
          top: 0; left: 0; right: 0;
          pointer-events: none;
        }
        .mhmc-panel-grades.slide-in {
          transform: translateX(0);
          opacity: 1;
          position: relative;
          pointer-events: auto;
        }
        .mhmc-course-row {
          cursor: pointer;
          transition: background-color 0.15s;
        }
        .mhmc-course-row:hover {
          background-color: rgba(128,128,128,0.05);
        }
      `}</style>

      <div
        style={{
          backgroundColor: bgMain,
          padding: "clamp(16px, 3vw, 36px) clamp(14px, 3vw, 28px)",
          fontFamily: "'Inter','Segoe UI',-apple-system,BlinkMacSystemFont,sans-serif",
          minHeight: "100%",
        }}
      >
        <div style={{ maxWidth: 1360, margin: "0 auto" }}>
          {/* ── Título ── */}
          <h2
            style={{
              fontSize: "clamp(18px, 2.5vw, 26px)",
              fontWeight: 800,
              color: textPrimary,
              margin: "0 0 20px 0",
              letterSpacing: "-0.03em",
              position: "relative",
              display: "inline-block",
            }}
          >
            Mis Hijos / Sus Cursos
            <div
              style={{
                position: "absolute",
                bottom: -6,
                left: 0,
                width: "40%",
                height: 3,
                backgroundColor: accent,
                borderRadius: 2,
              }}
            />
          </h2>

          <div className="mhmc-grid">
            {/* ── Sidebar: Select de estudiante ── */}
            <div className="mhmc-sidebar">
              <div
                style={{
                  border: `1px solid ${borderColor}`,
                  borderRadius: 12,
                  padding: "18px 16px",
                  backgroundColor: bgCard,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
                }}
              >
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: textSecondary,
                    margin: "0 0 12px 0",
                    textTransform: "uppercase",
                    letterSpacing: "0.07em",
                  }}
                >
                  Seleccionar Estudiante
                </p>
                <FormControl size="small" fullWidth>
                  {/* <InputLabel style={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b" }}>Estudiante</InputLabel> */}
                  <Select
                    value={selectedHijoId}
                    // label="Estudiante"
                    onChange={(e) => {
                      setSelectedHijoId(e.target.value);
                      setExpandedCourseId(null);
                    }}
                    style={{
                      fontSize: 13,
                      backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "#fff",
                      color: textPrimary,
                      borderRadius: 8,
                    }}
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: borderColor,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: accent,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: accent,
                      },
                      "& .MuiSvgIcon-root": { color: textSecondary },
                    }}
                  >
                    {HIJOS.map((h) => (
                      <MenuItem key={h.id} value={h.id} style={{ fontSize: 13 }}>
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          {/* <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: NIVEL_COLOR[h.nivelActividad],
                              flexShrink: 0,
                            }}
                          /> */}
                          {h.nombre}
                        </span>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>

            {/* ── Main: detalle del estudiante ── */}
            <div className="mhmc-main">
              <div
                style={{
                  border: `1px solid ${borderColor}`,
                  borderRadius: 14,
                  backgroundColor: bgCard,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                  overflow: "hidden",
                }}
              >
                {/* Franja de color */}
                <div
                  style={{
                    height: 5,
                    background: `linear-gradient(90deg, ${accent}, ${accent}66)`,
                  }}
                />

                <div style={{ padding: "22px 22px 20px" }}>
                  {/* Header: avatar grande + info */}
                  <div className="mhmc-student-header">
                    <StudentAvatar nombre={hijo.nombre} avatar={hijo.avatar} colors={colors} size={76} />

                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Nombre + badge estado en la misma línea */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          flexWrap: "wrap",
                          marginBottom: 8,
                        }}
                      >
                        <h3
                          style={{
                            fontSize: "clamp(17px, 2vw, 22px)",
                            fontWeight: 800,
                            color: textPrimary,
                            margin: 0,
                            letterSpacing: "-0.02em",
                          }}
                        >
                          {hijo.nombre}
                        </h3>
                      </div>

                      {/* Stats inline – justificados en todo el ancho */}
                      <div className="mhmc-stats-row">
                        <div className="mhmc-stat-item">
                          <StatItem
                            label="Nivel de actividad"
                            value={
                              <>
                                <BarChartIcon
                                  style={{
                                    fontSize: 15,
                                    color: NIVEL_COLOR[hijo.nivelActividad],
                                  }}
                                />
                                {hijo.nivelActividad}
                              </>
                            }
                            valueColor={NIVEL_COLOR[hijo.nivelActividad]}
                            isDark={isDark}
                          />
                        </div>
                        <div className="mhmc-stat-item">
                          <StatItem label="Último acceso" value={hijo.ultimoAcceso} isDark={isDark} />
                        </div>
                        <div className="mhmc-stat-separator" />
                        <div className="mhmc-stat-item">
                          <StatItem label="Días ausente" value={hijo.diasDesdeAcceso} isDark={isDark} />
                        </div>
                        <div className="mhmc-stat-item">
                          <StatItem
                            label="Alertas"
                            value={`${hijo.alertasCount} alertas`}
                            valueColor={hijo.alertasCount > 0 ? "#ef4444" : "#22c55e"}
                            isDark={isDark}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── Alertas / Estado / Baja Actividad ── */}
                  <div
                    className="mhmc-info-grid"
                    style={{ borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "#e2e8f0"}`, paddingTop: 14, marginTop: 14 }}
                  >
                    {/* Alertas Activas */}
                    <div
                      className="rounded-lg border p-[0px_14px_12px] max-h-[198.5px] overflow-y-auto"
                      style={{ backgroundColor: isDark ? "rgba(239,68,68,0.07)" : "#fef2f2", "--bs-border-color": "#ef444430" }}
                    >
                      <div className="flex items-center gap-2 mb-3 pt-[12px] pb-[6px]">
                        <NotificationsActiveIcon style={{ fontSize: 14, color: "#ef4444" }} />
                        <span style={{ fontSize: 10, fontWeight: 800, color: "#ef4444", textTransform: "uppercase", letterSpacing: "0.07em" }}>
                          Alertas Activas
                        </span>
                      </div>
                      {hijo.alertasActivas.length === 0 ? (
                        <p style={{ fontSize: 12, color: textSecondary, margin: 0, fontStyle: "italic" }}>Sin alertas recientes</p>
                      ) : (
                        <ul className="flex flex-col gap-1">
                          {hijo.alertasActivas.map((alerta, i) => (
                            <li key={i} className="flex gap-2">
                              <div style={{ backgroundColor: "#ef4444" }} className="size-[5px] rounded-full mt-[6px]" />
                              <span style={{ color: isDark ? "#fca5a5" : "#991b1b" }} className="text-xl">
                                {alerta.message}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {/* Baja Actividad */}
                    <div
                      className="rounded-lg border p-[0px_14px_12px] max-h-[198.5px] overflow-y-auto"
                      style={{ backgroundColor: isDark ? "rgba(245,158,11,0.07)" : "#fffbeb", "--bs-border-color": "#f59e0b30" }}
                    >
                      <div className="flex items-center gap-2 mb-3 pt-[12px] pb-[6px]">
                        <TrafficIcon style={{ fontSize: 14, color: "#f59e0b" }} />
                        <span style={{ fontSize: 10, fontWeight: 800, color: "#f59e0b", textTransform: "uppercase", letterSpacing: "0.07em" }}>
                          Baja Actividad
                        </span>
                      </div>
                      {hijo.bajaActividad.length === 0 ? (
                        <p
                          style={{
                            fontSize: 12,
                            color: textSecondary,
                            margin: 0,
                            fontStyle: "italic",
                          }}
                        >
                          Sin registros de riesgo
                        </p>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                          }}
                        >
                          {hijo.bajaActividad.map((item, i) => (
                            <div
                              key={i}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 10,
                                backgroundColor: isDark ? "rgba(245,158,11,0.1)" : "#fffbeb",
                                padding: "7px 11px",
                                borderRadius: 7,
                                border: "1px solid #f59e0b20",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: isDark ? "#e2e8f0" : "#1e293b",
                                }}
                              >
                                {item.nombre_curso}
                              </span>
                              <Badge color={RIESGO_COLOR[item.riesgo] || "#64748b"}>{item.riesgo}</Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Tabla de cursos ── */}
          <div
            style={{
              marginTop: 20,
              border: `1px solid ${borderColor}`,
              borderRadius: 14,
              overflow: "hidden",
              backgroundColor: bgCard,
              boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
            }}
          >
            <div
              style={{
                padding: "14px 20px",
                borderBottom: `1px solid ${borderColor}`,
                backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              {expandedCourseId ? (
                <button
                  onClick={() => setExpandedCourseId(null)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px 10px 4px 6px",
                    borderRadius: 8,
                    backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0",
                    color: textPrimary,
                    fontSize: 12,
                    fontWeight: 700,
                    transition: "background-color 0.15s",
                  }}
                >
                  <ArrowBackIcon style={{ fontSize: 15, color: accent }} />
                  Volver
                </button>
              ) : (
                <div style={{ width: 4, height: 18, backgroundColor: accent, borderRadius: 2 }} />
              )}
              <span style={{ fontSize: 14, fontWeight: 800, color: textPrimary }}>
                {expandedCourseId ? hijo.cursos.find((c) => `${hijo.id}-${c.id}` === expandedCourseId)?.nombre : "Cursos del Estudiante"}
              </span>
              {!expandedCourseId && (
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: 11,
                    fontWeight: 600,
                    color: textSecondary,
                    backgroundColor: isDark ? "rgba(255,255,255,0.07)" : "#e2e8f0",
                    borderRadius: 20,
                    padding: "2px 10px",
                  }}
                >
                  {hijo.cursos.length} cursos
                </span>
              )}
            </div>

            {/* ── Panels wrapper (slide) ── */}
            <div className="mhmc-panels-wrapper">
              {/* Panel: lista de cursos */}
              <div className={`mhmc-panel mhmc-panel-courses${expandedCourseId ? " slide-out" : ""}`}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }} className="[&_td]:border-0 [&_th]:border-0">
                    <thead>
                      <tr
                        className="mhmc-table-head"
                        style={{
                          backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#f1f5f9",
                          borderBottom: `2px solid ${borderColor}`,
                        }}
                      >
                        <th style={{ color: textSecondary }}>Curso</th>
                        <th style={{ color: textSecondary }}>Progreso</th>
                        <th style={{ color: textSecondary }}>Último acceso</th>
                        <th style={{ color: textSecondary, width: 40 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {hijo.cursos.map((curso, idx) => (
                        <tr
                          key={curso.id}
                          className="mhmc-table-body mhmc-course-row hover:ring-1 hover:ring-inset hover:ring-slate-300"
                          style={{ borderBottom: idx < hijo.cursos.length - 1 ? `1px solid ${borderColor}` : "none" }}
                          onClick={() => toggleCourse(curso.id, hijo.id)}
                        >
                          {/* Nombre */}
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                              <div
                                style={{
                                  width: 38,
                                  height: 38,
                                  borderRadius: 8,
                                  backgroundColor: isDark ? "rgba(255,255,255,0.07)" : "#e2e8f0",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  overflow: "hidden",
                                }}
                              >
                                {curso.img ? (
                                  <img src={curso.img} alt={curso.nombre} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                ) : (
                                  <span style={{ fontSize: 15, fontWeight: 800, color: textSecondary }}>{curso.nombre[0]}</span>
                                )}
                              </div>
                              <span style={{ fontSize: 13, fontWeight: 600, color: textPrimary, lineHeight: 1.4 }}>{curso.nombre}</span>
                            </div>
                          </td>

                          {/* Progreso */}
                          {curso.progreso !== null ? (
                            <td style={{ minWidth: 150 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={curso.progreso}
                                  sx={{
                                    flex: 1,
                                    height: 7,
                                    borderRadius: 4,
                                    backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0",
                                    "& .MuiLinearProgress-bar": {
                                      background: `linear-gradient(90deg, ${colors.primary_light} 0%, ${colors.tertiary_light} 100%)`,
                                      borderRadius: 4,
                                    },
                                  }}
                                />
                                <span
                                  style={{ fontSize: 12, fontWeight: 800, color: colors.backgroundColorCardBtn, minWidth: 34, textAlign: "right" }}
                                >
                                  {curso.progreso}%
                                </span>
                              </div>
                            </td>
                          ) : (
                            <td style={{ fontSize: 14, color: textSecondary, minWidth: 150, fontWeight: 700 }}>-</td>
                          )}

                          {/* Último acceso */}
                          <td style={{ fontSize: 12, color: textSecondary, minWidth: 150, fontWeight: 500 }}>{curso.ultimaActividad}</td>

                          {/* Arrow */}
                          <td style={{ textAlign: "center", width: 40 }}>
                            <ChevronRightIcon style={{ fontSize: 18, color: textSecondary }} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Panel: detalle de notas */}
              <div className={`mhmc-panel mhmc-panel-grades${expandedCourseId ? " slide-in" : ""}`}>
                {(() => {
                  const grades = courseGrades[expandedCourseId];
                  const isLoading = loadingGrades[expandedCourseId];
                  return isLoading ? (
                    <div style={{ padding: "32px 24px", textAlign: "center" }}>
                      <p style={{ fontSize: 13, color: textSecondary, margin: 0 }}>Cargando calificaciones...</p>
                    </div>
                  ) : !grades || grades.length === 0 ? (
                    <div style={{ padding: "32px 24px", textAlign: "center" }}>
                      <p style={{ fontSize: 13, color: textSecondary, margin: 0, fontStyle: "italic" }}>
                        Sin calificaciones disponibles para este curso.
                      </p>
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 380 }} className="[&_td]:border-0 [&_th]:border-0">
                        <thead>
                          <tr
                            className="mhmc-table-head"
                            style={{
                              backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#f1f5f9",
                              borderBottom: `2px solid ${borderColor}`,
                            }}
                          >
                            <th style={{ color: textSecondary }}>Evaluaciones</th>
                            <th style={{ color: textSecondary }}>Calificación</th>
                            <th style={{ color: textSecondary }}>Porcentaje</th>
                          </tr>
                        </thead>
                        <tbody>
                          {grades.map((d, i) => (
                            <tr
                              key={i}
                              className="mhmc-table-body"
                              style={{ borderBottom: i < grades.length - 1 ? `1px solid ${borderColor}` : "none" }}
                            >
                              <td style={{ fontSize: 13, color: textPrimary, fontWeight: 500 }}>{d.itemname}</td>
                              <td
                                style={{ fontSize: 13, color: textPrimary, fontWeight: 700 }}
                                dangerouslySetInnerHTML={{ __html: d.gradeformatted && d.gradeformatted.split("(")[0] }}
                              />
                              <td style={{ fontSize: 12, color: textSecondary }}>{d.percentageformatted}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
